<template>
  <div>
    <a-card style="margin-bottom:16px">
      <a-row :gutter="12" align="middle">
        <a-col>
          <a-select
            v-model:value="selectedNodeId"
            show-search
            placeholder="选择节点"
            style="width:280px"
            :options="nodeOptions"
            :filter-option="filterNodeOption"
            @change="loadProxies"
          />
        </a-col>
        <a-col>
          <a-select v-model:value="statusFilter" style="width:150px" @change="loadProxies">
            <a-select-option value="">运行中 + 创建中</a-select-option>
            <a-select-option value="active">仅运行中</a-select-option>
            <a-select-option value="creating">仅创建中</a-select-option>
            <a-select-option value="expired">已过期</a-select-option>
          </a-select>
        </a-col>
        <a-col>
          <a-button @click="refreshAll" :loading="loading">刷新</a-button>
        </a-col>
        <a-col v-if="selectedNode">
          <a-space :size="6" wrap>
            <a-tag :color="nodeStatusColor(selectedNode.status)">{{ nodeStatusLabel(selectedNode.status) }}</a-tag>
            <a-tag color="geekblue">{{ selectedNode.ip_public || '无公网 IP' }}</a-tag>
            <a-tag>端口 {{ selectedNode.used_ports }}/{{ selectedNode.capacity_ports }}</a-tag>
            <a-tag :color="selectedNode.active_clients > 0 ? 'blue' : 'default'">
              连接 {{ selectedNode.active_clients }}
            </a-tag>
          </a-space>
        </a-col>
      </a-row>
    </a-card>

    <a-row :gutter="16">
      <a-col :span="6">
        <a-card title="节点" size="small" :body-style="{ padding: '8px' }">
          <a-spin :spinning="nodesLoading">
            <a-list
              size="small"
              :data-source="nodes"
              :locale="{ emptyText: '暂无节点' }"
              style="max-height:680px;overflow:auto"
            >
              <template #renderItem="{ item }">
                <a-list-item
                  :style="nodeListItemStyle(item.id)"
                  @click="selectNode(item.id)"
                >
                  <div style="width:100%">
                    <div style="display:flex;align-items:center;justify-content:space-between;gap:8px">
                      <strong style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ item.name }}</strong>
                      <a-tag :color="nodeStatusColor(item.status)" style="margin:0">{{ nodeStatusLabel(item.status) }}</a-tag>
                    </div>
                    <div style="font-size:11px;color:#6b7280;margin-top:4px;font-family:ui-monospace,Menlo,monospace">
                      #{{ item.id }} · {{ item.ip_public || 'no-ip' }}
                    </div>
                    <div style="font-size:11px;color:#6b7280;margin-top:4px">
                      端口 {{ item.used_ports }}/{{ item.capacity_ports }} · 连接 {{ item.active_clients }}
                    </div>
                  </div>
                </a-list-item>
              </template>
            </a-list>
          </a-spin>
        </a-card>
      </a-col>

      <a-col :span="18">
        <a-card size="small" :body-style="{ padding: 0 }">
          <template #title>
            <span>{{ selectedNode ? `${selectedNode.name} 节点拓扑` : '节点拓扑' }}</span>
          </template>
          <template #extra>
            <span style="color:#6b7280;font-size:12px">
              {{ proxies.length }} 个节点 · 点击节点查看详情
            </span>
          </template>
          <div v-if="loading" style="height:680px;display:flex;align-items:center;justify-content:center">
            <a-spin />
          </div>
          <div
            v-else-if="!selectedNode"
            style="height:680px;display:flex;align-items:center;justify-content:center"
          >
            <a-empty description="请选择节点" />
          </div>
          <div
            v-else-if="proxies.length === 0"
            style="height:680px;display:flex;align-items:center;justify-content:center"
          >
            <a-empty description="当前筛选下没有节点" />
          </div>
          <div v-show="!loading && selectedNode && proxies.length > 0" ref="chartRef" style="width:100%;height:680px" />
        </a-card>
      </a-col>
    </a-row>

    <ProxyDetailDrawer v-model:visible="drawerVisible" :proxy="selectedProxy" />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import * as echarts from 'echarts';
import { listNodes } from '../api/nodes';
import { listProxies } from '../api/proxies';
import ProxyDetailDrawer from '../components/ProxyDetailDrawer.vue';
import type { NodeItem, ProxyItem } from '../types';
import { formatTrafficBytes } from '../utils/trafficFormat';

const nodes = ref<NodeItem[]>([]);
const proxies = ref<ProxyItem[]>([]);
const selectedNodeId = ref<number | undefined>();
const statusFilter = ref('');
const nodesLoading = ref(false);
const loading = ref(false);
const chartRef = ref<HTMLElement | null>(null);
const drawerVisible = ref(false);
const selectedProxy = ref<ProxyItem | null>(null);

let chart: echarts.ECharts | null = null;
let resizeObserver: ResizeObserver | undefined;

const selectedNode = computed(() => nodes.value.find((n) => n.id === selectedNodeId.value) || null);

const nodeOptions = computed(() => nodes.value.map((n) => ({
  label: `${n.name} (#${n.id})`,
  value: n.id,
})));

const filterNodeOption = (input: string, option: any) =>
  String(option?.label || '').toLowerCase().includes(input.toLowerCase());

const nodeListItemStyle = (id: number) => ({
  cursor: 'pointer',
  borderRadius: '6px',
  padding: '10px',
  background: id === selectedNodeId.value ? '#eff6ff' : 'transparent',
  border: id === selectedNodeId.value ? '1px solid #bfdbfe' : '1px solid transparent',
});

const nodeStatusColor = (status: string) => {
  switch (status) {
    case 'online': return 'green';
    case 'new': return 'blue';
    case 'offline': return 'default';
    default: return 'default';
  }
};

const nodeStatusLabel = (status: string) => {
  switch (status) {
    case 'online': return '在线';
    case 'new': return '未加入';
    case 'offline': return '离线';
    default: return status;
  }
};

const proxyStatusColor = (status: string) => {
  switch (status) {
    case 'active': return '#16a34a';
    case 'creating': return '#2563eb';
    case 'expired': return '#f97316';
    case 'deleted': return '#dc2626';
    default: return '#64748b';
  }
};

const proxyStatusLabel = (status: string) => {
  switch (status) {
    case 'active': return '运行中';
    case 'creating': return '创建中';
    case 'expired': return '已过期';
    case 'deleted': return '已删除';
    default: return status;
  }
};

const formatDateTime = (s?: string) => {
  if (!s) return '';
  return s.replace('T', ' ').replace(/\+\d{2}:\d{2}$|Z$/, '');
};

const formatBytes = formatTrafficBytes;

const selectNode = (id: number) => {
  if (selectedNodeId.value === id) return;
  selectedNodeId.value = id;
  loadProxies();
};

const loadNodes = async () => {
  nodesLoading.value = true;
  try {
    const r = await listNodes();
    nodes.value = r.data || [];
    if (!selectedNodeId.value && nodes.value.length > 0) {
      selectedNodeId.value = nodes.value[0].id;
    }
  } catch (e: any) {
    message.error(e.response?.data?.error?.message || '加载节点失败');
  } finally {
    nodesLoading.value = false;
  }
};

const loadProxies = async () => {
  if (!selectedNodeId.value) {
    proxies.value = [];
    disposeChart();
    return;
  }
  loading.value = true;
  try {
    const params: Record<string, any> = { node_id: selectedNodeId.value };
    if (statusFilter.value) params.status = statusFilter.value;
    const r = await listProxies(params);
    const next: ProxyItem[] = r.data || [];
    proxies.value = next;
    if (selectedProxy.value) {
      const refreshed = next.find((p) => p.proxy_id === selectedProxy.value?.proxy_id);
      if (refreshed) selectedProxy.value = refreshed;
    }
  } catch (e: any) {
    message.error(e.response?.data?.error?.message || '加载节点失败');
  } finally {
    loading.value = false;
  }
  await renderChart();
};

const refreshAll = async () => {
  await loadNodes();
  await loadProxies();
};

const buildGraphOption = () => {
  const node = selectedNode.value;
  if (!node) return {};
  const centerId = `node-${node.id}`;
  const graphNodes: any[] = [{
    id: centerId,
    name: node.name,
    value: [
      `节点 #${node.id}`,
      node.ip_public || '无公网 IP',
      `${node.used_ports}/${node.capacity_ports} 端口`,
      `${node.active_clients} 连接`,
    ].join('\n'),
    x: 0,
    y: 0,
    symbolSize: 112,
    category: 0,
    itemStyle: {
      color: node.status === 'online' ? '#1677ff' : '#94a3b8',
      borderColor: '#fff',
      borderWidth: 3,
    },
    label: {
      color: '#fff',
      fontWeight: 700,
      lineHeight: 18,
      formatter: () => `${node.name}\n#${node.id}`,
    },
  }];
  const links: any[] = [];
  const total = proxies.value.length;
  const radius = total <= 5 ? 240 : 300;
  proxies.value.forEach((p, index) => {
    const angle = total === 1 ? 0 : (Math.PI * 2 * index) / total;
    const activeBoost = p.active_clients > 0 ? 10 : 0;
    const proxyId = `proxy-${p.proxy_id}`;
    const regionNames = p.region_names?.length ? p.region_names : [p.region_name].filter(Boolean);
    graphNodes.push({
      id: proxyId,
      name: `#${p.proxy_id}`,
      proxyId: p.proxy_id,
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius,
      symbolSize: 76 + activeBoost,
      category: 1,
      itemStyle: {
        color: proxyStatusColor(p.status),
        borderColor: p.active_clients > 0 ? '#0f172a' : '#fff',
        borderWidth: p.active_clients > 0 ? 3 : 2,
      },
      label: {
        color: '#0f172a',
        fontSize: 11,
        lineHeight: 16,
        formatter: () => [
          regionNames.slice(0, 2).join('/'),
          `:${p.port}`,
          proxyStatusLabel(p.status),
        ].filter(Boolean).join('\n'),
      },
      tooltipLines: [
        `节点 #${p.proxy_id}`,
        `大区：${regionNames.join(' / ') || '-'}`,
        `端口：${p.port}`,
        `账号：${p.username}`,
        `状态：${proxyStatusLabel(p.status)}`,
        `连接：${p.active_clients || 0}`,
        `上行：${formatBytes(p.bytes_up || 0)}`,
        `下行：${formatBytes(p.bytes_down || 0)}`,
        `到期：${formatDateTime(p.expire_at)}`,
        p.note ? `备注：${p.note}` : '',
      ].filter(Boolean),
    });
    links.push({
      source: centerId,
      target: proxyId,
      lineStyle: {
        color: p.active_clients > 0 ? '#1677ff' : '#cbd5e1',
        width: p.active_clients > 0 ? 3 : 1,
        curveness: 0.08,
      },
    });
  });
  return {
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: (params: any) => {
        if (params.data?.tooltipLines) return params.data.tooltipLines.join('<br/>');
        if (params.data?.value) return String(params.data.value).replace(/\n/g, '<br/>');
        return params.name || '';
      },
    },
    legend: {
      top: 16,
      right: 24,
      data: ['服务器', '节点'],
    },
    series: [{
      type: 'graph',
      layout: 'none',
      roam: true,
      draggable: true,
      categories: [{ name: '服务器' }, { name: '节点' }],
      data: graphNodes,
      links,
      edgeSymbol: ['none', 'arrow'],
      edgeSymbolSize: 8,
      label: {
        show: true,
        position: 'bottom',
      },
      emphasis: {
        focus: 'adjacency',
        lineStyle: { width: 4 },
      },
    }],
  };
};

const initChart = async () => {
  await nextTick();
  if (!chartRef.value || chart) return;
  if (chartRef.value.clientWidth === 0) return;
  chart = echarts.init(chartRef.value);
  chart.on('click', (params: any) => {
    const proxyId = params.data?.proxyId;
    if (!proxyId) return;
    const p = proxies.value.find((x) => x.proxy_id === proxyId);
    if (!p) return;
    selectedProxy.value = p;
    drawerVisible.value = true;
  });
  resizeObserver = new ResizeObserver(() => chart?.resize());
  resizeObserver.observe(chartRef.value);
};

const renderChart = async () => {
  if (!selectedNode.value || proxies.value.length === 0) {
    disposeChart();
    return;
  }
  await initChart();
  if (!chart) return;
  chart.setOption(buildGraphOption(), true);
  chart.resize();
};

const disposeChart = () => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = undefined;
  }
  if (chart) {
    chart.dispose();
    chart = null;
  }
};

watch(() => [selectedNode.value?.id, proxies.value.length], () => {
  renderChart();
});

onMounted(async () => {
  await loadNodes();
  await loadProxies();
});

onUnmounted(disposeChart);
</script>
